// src/pages/admin/AdminAnalytics.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from './components/AdminSidebar';
import { Chart } from 'react-google-charts';
import '../../styles/admin.css';

function AdminAnalytics() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    deviceCount: 0,
    repairCount: 0,
    categoryCount: 0,
    brandStats: []
  });
  const [repairStats, setRepairStats] = useState([]);
  const [deviceStats, setDeviceStats] = useState([]);
  
  useEffect(() => {
    fetchAnalytics();
  }, []);
  
  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        navigate('/admin/login');
        return;
      }
      
      // Fetch devices directly
      const devicesResponse = await fetch('/api/admin/devices', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!devicesResponse.ok) {
        if (devicesResponse.status === 401) {
          localStorage.removeItem('adminToken');
          navigate('/admin/login');
          return;
        }
        throw new Error('Failed to fetch devices');
      }
      
      const devices = await devicesResponse.json();
      
      // Calculate stats from devices
      let totalRepairs = 0;
      const brands = new Set();
      const deviceTypes = new Set();
      
      devices.forEach(device => {
        totalRepairs += (device.repairs?.length || 0);
        if (device.brand) brands.add(device.brand);
        if (device.type) deviceTypes.add(device.type);
      });
      
      setStats({
        deviceCount: devices.length,
        repairCount: totalRepairs,
        categoryCount: deviceTypes.size,
        brandCount: brands.size
      });
      
      // Process device statistics
      processDeviceStats(devices);
      processRepairStats(devices);
      
    } catch (err) {
      console.error('Error fetching analytics:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const processDeviceStats = (devices) => {
    // Count devices by type
    const deviceTypeCount = devices.reduce((acc, device) => {
      acc[device.type] = (acc[device.type] || 0) + 1;
      return acc;
    }, {});
    
    // Count devices by brand
    const brandCount = devices.reduce((acc, device) => {
      acc[device.brand] = (acc[device.brand] || 0) + 1;
      return acc;
    }, {});
    
    // Convert to array format for charts
    const deviceData = Object.entries(deviceTypeCount).map(([type, count]) => [
      type.charAt(0).toUpperCase() + type.slice(1),
      count
    ]);
    
    setDeviceStats({
      byType: [['Device Type', 'Count'], ...deviceData],
      byBrand: Object.entries(brandCount)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10) // Top 10 brands
    });
  };
  
  const processRepairStats = (devices) => {
    // Count repairs by type
    const repairTypeCount = {};
    let totalRepairs = 0;
    
    devices.forEach(device => {
      if (device.repairs && device.repairs.length > 0) {
        device.repairs.forEach(repair => {
          repairTypeCount[repair.repair] = (repairTypeCount[repair.repair] || 0) + 1;
          totalRepairs++;
        });
      }
    });
    
    // Get top repair types
    const topRepairs = Object.entries(repairTypeCount)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([type, count]) => ({
        type,
        count,
        percentage: ((count / totalRepairs) * 100).toFixed(1)
      }));
    
    setRepairStats(topRepairs);
  };
  
  if (loading) {
    return (
      <div className="admin-layout">
        <AdminSidebar activeItem="analytics" />
        <div className="admin-content">
          <div className="loading">Loading analytics...</div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="admin-layout">
      <AdminSidebar activeItem="analytics" />
      
      <div className="admin-content">
        <h1>Analytics Dashboard</h1>
        
        {/* Overview Stats */}
        <div className="dashboard-stats">
          <div className="stat-card">
            <h3>Total Devices</h3>
            <div className="stat-number">{stats.deviceCount}</div>
          </div>
          
          <div className="stat-card">
            <h3>Total Repairs</h3>
            <div className="stat-number">{stats.repairCount}</div>
          </div>
          
          <div className="stat-card">
            <h3>Device Categories</h3>
            <div className="stat-number">{stats.categoryCount}</div>
          </div>
        </div>
        
        {/* Charts Section */}
        <div className="analytics-charts">
          {/* Device Distribution Chart */}
          {deviceStats.byType && deviceStats.byType.length > 1 && (
            <div className="chart-container">
              <h2>Device Distribution by Type</h2>
              <Chart
                chartType="PieChart"
                data={deviceStats.byType}
                options={{
                  title: '',
                  pieHole: 0.4,
                  colors: ['#007BFF', '#28a745', '#ffc107', '#dc3545'],
                  legend: { position: 'bottom' }
                }}
                width="100%"
                height="300px"
              />
            </div>
          )}
          
          {/* Top Brands */}
          {deviceStats.byBrand && deviceStats.byBrand.length > 0 && (
            <div className="chart-container">
              <h2>Top Device Brands</h2>
              <Chart
                chartType="BarChart"
                data={[
                  ['Brand', 'Number of Devices'],
                  ...deviceStats.byBrand.map(([brand, count]) => [brand, count])
                ]}
                options={{
                  title: '',
                  chartArea: { width: '70%' },
                  hAxis: {
                    title: 'Number of Devices',
                    minValue: 0
                  },
                  vAxis: {
                    title: 'Brand'
                  },
                  colors: ['#007BFF']
                }}
                width="100%"
                height="400px"
              />
            </div>
          )}
        </div>
        
        {/* Repair Statistics */}
        {repairStats.length > 0 && (
          <div className="repair-stats-section">
            <h2>Top Repair Types</h2>
            <div className="repair-stats-grid">
              {repairStats.map((repair, index) => (
                <div key={index} className="repair-stat-item">
                  <div className="repair-stat-header">
                    <span className="repair-type">{repair.type}</span>
                    <span className="repair-percentage">{repair.percentage}%</span>
                  </div>
                  <div className="repair-stat-bar">
                    <div 
                      className="repair-stat-fill"
                      style={{ width: `${repair.percentage}%` }}
                    />
                  </div>
                  <div className="repair-count">{repair.count} devices</div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Additional Insights */}
        <div className="insights-section">
          <h2>Key Insights</h2>
          <div className="insights-grid">
            <div className="insight-card">
              <h3>Most Common Device Type</h3>
              <p>{deviceStats.byType && deviceStats.byType[1] ? deviceStats.byType[1][0] : 'N/A'}</p>
            </div>
            
            <div className="insight-card">
              <h3>Leading Brand</h3>
              <p>{deviceStats.byBrand && deviceStats.byBrand[0] ? deviceStats.byBrand[0][0] : 'N/A'}</p>
            </div>
            
            <div className="insight-card">
              <h3>Most Common Repair</h3>
              <p>{repairStats[0] ? repairStats[0].type : 'N/A'}</p>
            </div>
            
            <div className="insight-card">
              <h3>Average Repairs per Device</h3>
              <p>{stats.deviceCount > 0 ? (stats.repairCount / stats.deviceCount).toFixed(1) : '0'}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminAnalytics;